import React from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  TrendingUp, 
  Shield, 
  Users, 
  Zap,
  ArrowRight,
  CheckCircle,
  Star,
  Rocket,
  Sparkles,
  Award,
  DollarSign,
  Crown,
  Target
} from "lucide-react";
import { motion } from "framer-motion";

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero Section - Ultra Modern */}
      <section className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-blue-900 text-white py-32 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1920')] opacity-5 bg-cover bg-center"></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            <Badge className="mb-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-900 border-0 text-base px-6 py-2 font-bold shadow-2xl">
              <Crown className="w-4 h-4 mr-2 inline" />
              💎 #1 Formation Crypto MLM Francophone
            </Badge>
            
            <h1 className="text-6xl md:text-8xl font-black leading-tight tracking-tight">
              De <span className="text-red-500">0€</span> à la<br/>
              <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent drop-shadow-2xl">
                LIBERTÉ FINANCIÈRE
              </span>
            </h1>
            
            <p className="text-2xl md:text-3xl font-bold text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Rejoignez les <span className="text-yellow-400">+500 membres</span> qui génèrent des revenus passifs 
              avec les <span className="text-green-400">meilleures opportunités Crypto MLM 2025</span>
            </p>

            <div className="flex flex-wrap gap-6 justify-center pt-8">
              <Link to={createPageUrl("Contact")}>
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 hover:from-yellow-500 hover:via-orange-600 hover:to-red-700 text-slate-900 text-xl px-12 py-8 shadow-2xl font-black rounded-2xl border-4 border-yellow-300 hover:scale-105 transition-transform">
                  <Rocket className="w-6 h-6 mr-3" />
                  DÉMARRER GRATUITEMENT
                  <ArrowRight className="w-6 h-6 ml-3" />
                </Button>
              </Link>
            </div>

            <div className="pt-6 flex flex-wrap gap-6 justify-center items-center text-sm">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-semibold">100% Gratuit</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-semibold">Sans Engagement</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-semibold">Résultats Prouvés</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats - Modern Design */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-900 border-0">
              📊 Résultats Réels & Vérifiables
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Des Chiffres Qui Parlent
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, label: "Membres Actifs", value: "500+", color: "from-blue-500 to-cyan-600" },
              { icon: DollarSign, label: "Revenus Générés", value: "2.5M€+", color: "from-green-500 to-emerald-600" },
              { icon: Award, label: "Satisfaction", value: "98%", color: "from-yellow-500 to-orange-600" },
              { icon: Sparkles, label: "Support", value: "24/7", color: "from-purple-500 to-pink-600" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-0 shadow-xl hover:shadow-2xl transition-all group cursor-pointer bg-gradient-to-br from-white to-slate-50">
                  <CardContent className="p-8 text-center">
                    <div className={`w-20 h-20 bg-gradient-to-r ${stat.color} rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                      <stat.icon className="w-10 h-10 text-white" />
                    </div>
                    <p className="text-5xl font-black bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-2">
                      {stat.value}
                    </p>
                    <p className="text-slate-600 font-semibold text-lg">{stat.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits - Ultra Impact */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 via-purple-900/50 to-pink-900/50"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-900 text-base px-6 py-2 font-bold">
              <Zap className="w-4 h-4 mr-2 inline" />
              Système Éprouvé & Duplicable
            </Badge>
            <h2 className="text-5xl md:text-6xl font-black mb-6">
              Pourquoi Vous Allez<br/>
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                RÉUSSIR AVEC NOUS
              </span>
            </h2>
            <p className="text-2xl text-blue-100 max-w-3xl mx-auto">
              Un écosystème complet pour transformer votre vie financière
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: DollarSign,
                title: "Revenus Passifs AUTOMATIQUES",
                description: "Smart contracts + MLM = Revenus 24/7 même en dormant. Certains membres gagnent 500-2000€/mois dès le 2ème mois",
                color: "from-green-500 to-emerald-600",
                badge: "💰 LE PLUS IMPORTANT"
              },
              {
                icon: Rocket,
                title: "Démarrage IMMÉDIAT 0€",
                description: "WeFi = 0€ d'investissement. Formation offerte. Accompagnement inclus. Vous pouvez commencer MAINTENANT sans risque",
                color: "from-blue-500 to-cyan-600",
                badge: "🚀 PARFAIT DÉBUTANTS"
              },
              {
                icon: Crown,
                title: "Système CLÉS EN MAIN",
                description: "Tout est fourni : outils, formation, scripts, support 24/7. Il suffit de SUIVRE le plan pour réussir",
                color: "from-purple-500 to-pink-600",
                badge: "👑 DUPLICABLE À 100%"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm hover:scale-105 transition-transform group h-full">
                  <CardHeader>
                    <Badge className="mb-4 bg-yellow-400 text-slate-900 w-fit text-xs font-bold">
                      {benefit.badge}
                    </Badge>
                    <div className={`w-20 h-20 bg-gradient-to-r ${benefit.color} rounded-3xl flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 transition-transform`}>
                      <benefit.icon className="w-10 h-10 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-black text-slate-900">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 text-lg leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            Prêt à Transformer Votre Vie ?
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Bénéficiez d'un accompagnement personnalisé et d'opportunités vérifiées
          </p>
          <Link to={createPageUrl("Contact")}>
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-10 py-7 shadow-xl">
              Commencer Maintenant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
